/* eslint-disable max-len */
import { orderBy } from 'lodash/fp';
import { createContext, useContext, useEffect, useMemo } from 'react';
import urljoin from 'url-join';
import { type EventApplicationEntrypointContextDataFragment } from '../../../../api/fragments/EventApplicationEntrypointContextData';
import { type EventDataFragment } from '../../../../api/fragments/EventData';
import { type VehicleCalculatorSpecsFragment } from '../../../../api/fragments/VehicleCalculatorSpecs';
import { useListLocalVariantsForCalculatorQuery } from '../../../../api/queries/listLocalVariantsForCalculator';
import { ApplicationScenario, type EventCustomizedFieldInput, Purpose } from '../../../../api/types';
import { useAccount } from '../../../../components/contexts/AccountContextManager';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import NotFoundResult from '../../../../components/results/NotFoundResult';
import { useHeaderContext } from '../../../../layouts/HeaderContextManager';
import useActiveState from '../../../../utilities/useActiveStage';
import { JourneyStage } from '../../StandardApplicationEntrypoint/Journey/JourneyController';
import { type EventApplicationState } from '../Journey/JourneyController';
import TimeoutPage from '../TimeoutPage';

type EventJourneySetupContextProps = {
    event: EventDataFragment;
    endpoint: EventApplicationEntrypointContextDataFragment;
    variants: Array<Extract<VehicleCalculatorSpecsFragment, { __typename: 'LocalVariant' }>> | null;
    eventModule: Extract<EventDataFragment['module'], { __typename: 'EventApplicationModule' }>;
    customizedFieldsInput: EventCustomizedFieldInput[];
    showLiveChat: boolean;
    showResetKYCButton: boolean;
    liveChatSetting?: Extract<EventDataFragment['module'], { __typename: 'EventApplicationModule' }>['liveChatSetting'];
    journeyStages?: JourneyStage[];
};

const EventJourneySetupContext = createContext<EventJourneySetupContextProps>(null);

type EventJourneySetupProviderProps = {
    endpoint: EventApplicationEntrypointContextDataFragment;
    event: EventDataFragment;
    children?: React.ReactNode;
    application?: EventApplicationState;
};

export const EventJourneySetupProvider = ({
    event,
    children,
    endpoint,
    application,
}: EventJourneySetupProviderProps) => {
    // Fetch variants data
    const { data: variantData, loading: loadingVariant } = useListLocalVariantsForCalculatorQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                purpose: Purpose.Production,
                moduleId: endpoint.eventApplicationModule.vehicleModuleId,
                applicationModuleId: endpoint.eventApplicationModule.id,
                dealerIds: event.dealerIds,
            },
        },
    });

    // Get current user
    const user = useAccount(true);

    // Detect idle state
    const { isActive, reset } = useActiveState();

    const variants = useMemo(() => {
        if (!variantData) {
            return null;
        }

        return variantData.list.items.filter(item => item.__typename === 'LocalVariant');
    }, [variantData]);

    // obtained from `scenarios` as application is not initialized
    const draftJourneyStages = useMemo(() => {
        const { scenarios = [] } = event;

        return [
            JourneyStage.ApplicantKYC,
            ...(scenarios?.includes(ApplicationScenario.Payment) ? [JourneyStage.Deposit] : []),
        ];
    }, [event]);

    // Memoize necessary values
    const values = useMemo((): EventJourneySetupContextProps => {
        const eventCustomizedFieldsInput = event.customizedFields
            ? orderBy('order', 'asc', event.customizedFields).map(({ id, order, __typename, ...rest }) => ({
                  ...rest,
                  value: '',
              }))
            : [];

        return {
            event,
            endpoint,
            variants,
            journeyStages: draftJourneyStages,
            eventModule: event.module.__typename === 'EventApplicationModule' ? event.module : null,
            customizedFieldsInput:
                (application?.customizedFields as EventCustomizedFieldInput[]) ?? eventCustomizedFieldsInput,
            showLiveChat: event?.showLiveChat && endpoint.eventApplicationModule?.liveChatSetting && !user,
            liveChatSetting:
                event?.module.__typename === 'EventApplicationModule' ? event.module.liveChatSetting : null,
            showResetKYCButton:
                event?.module.__typename === 'EventApplicationModule' ? event.module.showResetKYCButton : false,
        };
    }, [event, endpoint, variants, draftJourneyStages, application?.customizedFields, user]);

    const { setLink } = useHeaderContext();

    useEffect(() => {
        // if public access, we make LGF the home page
        if (!event.privateAccess) {
            setLink(urljoin(endpoint.pathname, event.urlSlug));
        }
    }, [endpoint.pathname, event, setLink]);

    // Read loading variants
    if (loadingVariant) {
        return <PortalLoadingElement />;
    }

    if (!isActive && !user) {
        return <TimeoutPage onBack={reset} />;
    }

    // Check module is the same
    if (endpoint.eventApplicationModule.id !== event.module.id) {
        return <NotFoundResult />;
    }

    return <EventJourneySetupContext.Provider value={values}>{children}</EventJourneySetupContext.Provider>;
};

export const useEventJourneySetupContext = () => {
    const context = useContext(EventJourneySetupContext);

    if (!context) {
        throw new Error('EventJourneySetupContext is missing');
    }

    return context;
};
